
import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { faCheck, faCircleInfo, faTriangleExclamation, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertProps, AlertTitle, Box, Slide, Snackbar, Typography, styled } from '@mui/material';
import AlertContext, { IAlert } from '../../../contexts/styles/AlertContext';
import { IHasChildren } from '../../common/types/IHasChildren';


interface IAlertProviderProps extends IHasChildren { }


const StyledAlert = styled(Alert, { shouldForwardProp: (props) => props !== "styleSeverity" })<AlertProps & { styleSeverity?: "warning" | "info" | "success" | "error" }>(({ styleSeverity, theme }) => ({
    borderRadius: '10px',
    border: `1px solid ${theme.palette[styleSeverity ?? "info"].dark}55`,
    maxWidth: '550px',
}));

const AlertProvider: FC<IAlertProviderProps> = ({
    children
}) => {
    const [alerts, setAlerts] = useState<IAlert[]>([]);
    const timerIds = useRef<number[]>([]);

    const removeAlert = useCallback((alert: IAlert) => {
        // Set alert to 'dismissed'
        setAlerts(c => {
            var index = c.indexOf(alert);
    
            if (index === -1) {
                return c;
            }

            const copy = [...c];

            copy[index].dismissed = true;

            return copy;
        });

        // in '1sec' remove the alert from the list
        const timer: { id?: number; } = {
            id: undefined
        };

        timer.id = window.setTimeout(() => {
            setAlerts(c => {
                var index = c.indexOf(alert);
    
                if (index === -1) {
                    return c;
                }

                return c.filter((_, i) => i !== index);
            })
            timerIds.current = timerIds.current.filter(t => t !== timer.id);

        }, 1000);

        timerIds.current.push(timer.id);

    }, [alerts]);

    const addAlert = useCallback((alert: IAlert) => {
        if (alert.timeout) {
            const timer: { id?: number; } = {
                id: undefined
            };

            timer.id = window.setTimeout(() => {
                removeAlert(alert);
                timerIds.current = timerIds.current.filter(t => t !== timer.id);
            }, alert.timeout);

            timerIds.current.push(timer.id);
        }

        setAlerts(c => {
            return [
                ...c,
                alert
            ]
        });


    }, [alerts, removeAlert]);

    const context = useMemo(() => {
        return {
            alerts,
            addAlert,
            removeAlert
        }
    }, [alerts, addAlert, removeAlert]);

    return (
        <AlertContext.Provider value={context}>
            {children}
            <Snackbar style={{ marginTop: '40px', zIndex: 20 }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={alerts.length !== 0}>
                <Box>
                    {
                        alerts.map((alert) => (
                            <Slide
                                direction='left'
                                in={!alert.dismissed}
                                mountOnEnter 
                                unmountOnExit
                            >
                                <StyledAlert
                                    icon={
                                        alert.severity === 'success' ? 
                                            <FontAwesomeIcon icon={faCheck} /> : 
                                        alert.severity === 'error' ? 
                                            <FontAwesomeIcon icon={faXmark} /> : 
                                        alert.severity === 'warning' ?
                                            <FontAwesomeIcon icon={faTriangleExclamation} /> :
                                            <FontAwesomeIcon icon={faCircleInfo} />
                                    }
                                    onClose={alert.dismissable ? () => removeAlert(alert) : undefined} 
                                    severity={alert.severity ?? 'info'}
                                    styleSeverity={alert.severity ?? 'info'}
                                    sx={{ mt: 2 }}
                                >
                                    <AlertTitle>{alert.severity ? (alert.severity[0].toUpperCase() + alert.severity.slice(1)) : 'Info'}</AlertTitle>
                                    <Typography variant='body2'>{alert.content}</Typography>
                                </StyledAlert>
                            </Slide>
                        ))
                    }
                </Box>
            </Snackbar>
        </AlertContext.Provider>
    )
}

export default AlertProvider